import { el, clear } from '../../core/dom.js';
import { openModal } from '../../components/modal.js';
import { toast } from '../../components/toast.js';
import { empty } from '../../components/empty.js';
import { errorPanel } from '../../components/error.js';
import { button } from '../../components/button.js';
import { skeleton } from '../../components/skeleton.js';
import { withProduct } from '../../core/route-map.js';
import { armDestructive, resetDestructive } from './confirm.js';
import {
  archiveProduct, listProducts, restoreProduct, updateProduct,
} from './actions.js';
import { PAGE_SIZE, pageOf, productIsArchived, productStatusChip } from './util.js';

/**
 * @param {{
 *   host: HTMLElement,
 *   me: object,
 *   isAdmin: boolean,
 *   productId: string,
 *   dead: () => boolean,
 * }} ctx
 */
export function mountProducts(ctx) {
  const state = {
    status: 'active',
    rows: [],
    shown: PAGE_SIZE,
    loading: true,
    fatal: '',
    modal: null,
  };

  function paint() {
    if (ctx.dead()) return;
    clear(ctx.host);
    if (state.loading) {
      ctx.host.append(skeleton(5));
      return;
    }
    if (state.fatal) {
      ctx.host.append(errorPanel({
        title: 'Products unavailable',
        message: state.fatal,
        action: button('Try again', { variant: 'primary', onClick: () => load(true) }),
      }));
      return;
    }
    ctx.host.append(productsPanel(state, ctx, {
      onStatus: (status) => {
        state.status = status;
        state.shown = PAGE_SIZE;
        load(true);
      },
      onMore: () => { state.shown += PAGE_SIZE; paint(); },
      onEdit: (row) => openEdit(row),
      onRestore: (row) => restore(row),
    }));
  }

  function openEdit(row) {
    if (state.modal) state.modal.close();
    state.modal = openEditProduct({
      row,
      isAdmin: ctx.isAdmin,
      onDone: () => load(false),
      onClose: () => { state.modal = null; },
    });
  }

  async function restore(row) {
    const result = await restoreProduct(row.id);
    if (ctx.dead()) return;
    if (!result || result.error) {
      toast((result && result.error) || 'Could not restore product', 'error');
      return;
    }
    toast('Product restored');
    await load(false);
  }

  async function load(initial) {
    if (initial) {
      state.loading = true;
      state.fatal = '';
      paint();
    }
    const data = await listProducts(state.status);
    if (ctx.dead()) return;
    state.loading = false;
    if (!data || data.error) {
      state.fatal = (data && data.error) || 'Could not load products';
      state.rows = [];
    } else {
      state.fatal = '';
      state.rows = data.products || [];
    }
    paint();
  }

  paint();
  load(true);
  return {
    refresh: () => load(false),
    dispose() { if (state.modal) state.modal.close(); },
  };
}

function productsPanel(state, ctx, handlers) {
  const rows = pageOf(state.rows, state.shown);
  const archived = state.status === 'archived';
  return el('div', { class: 'set-stack' },
    el('div', { class: 'set-tabs', role: 'tablist' },
      tab('Active', !archived, () => handlers.onStatus('active')),
      tab('Archived', archived, () => handlers.onStatus('archived'))),
    rows.length
      ? el('div', { class: 'set-product-list' }, rows.map((row) => productCard(row, ctx, handlers)))
      : empty({
        title: archived ? 'No archived products' : 'No products yet',
        body: archived
          ? 'Archived products appear here and can be restored anytime.'
          : 'Add a product to start connecting sources and chatting.',
        action: !archived && ctx.isAdmin
          ? button('Add Product', {
            variant: 'primary',
            href: withProduct('/app/products/new', ctx.productId),
          })
          : null,
      }),
    state.rows.length > rows.length
      ? el('div', { class: 'set-panel-foot lbl' },
        el('span', {}, `Showing ${rows.length} of ${state.rows.length}`),
        el('button', { type: 'button', class: 'set-more', onClick: handlers.onMore },
          'View all ', el('span', { class: 'set-arw', 'aria-hidden': 'true' }, '→')))
      : null);
}

function tab(label, on, onClick) {
  return el('button', {
    type: 'button',
    class: on ? 'set-tab is-on' : 'set-tab',
    role: 'tab',
    'aria-selected': on ? 'true' : 'false',
    onClick,
  }, label);
}

function productCard(row, ctx, handlers) {
  const archived = productIsArchived(row);
  return el('article', { class: archived ? 'set-product is-archived' : 'set-product' },
    el('div', { class: 'set-product-copy' },
      el('h2', {}, row.name || row.slug || 'Product'),
      el('p', {}, row.description || row.short_intro || `Slug · ${row.slug || '-'}`),
      el('div', { class: 'set-product-meta' },
        el('span', { class: 'set-chip' }, productStatusChip(row)),
        row.integration_count != null
          ? el('span', { class: 'set-chip' }, `${row.integration_count} connectors`)
          : null)),
    el('div', { class: 'set-product-actions' },
      el('a', {
        class: 'set-btn',
        href: withProduct(`/app/products/${encodeURIComponent(row.id)}`, ctx.productId),
      }, 'Open'),
      ctx.isAdmin && !archived
        ? el('button', {
          type: 'button',
          class: 'set-btn',
          onClick: () => handlers.onEdit(row),
        }, 'Edit')
        : null,
      ctx.isAdmin && archived
        ? el('button', {
          type: 'button',
          class: 'set-btn set-btn-primary',
          onClick: () => handlers.onRestore(row),
        }, 'Restore')
        : null,
      !ctx.isAdmin
        ? el('span', { class: 'set-hint' }, 'Ask an admin to edit or archive.')
        : null));
}

function openEditProduct(opts) {
  const row = opts.row;
  const form = {
    name: row.name || '',
    slug: row.slug || '',
    description: row.description || '',
  };
  const err = el('p', { class: 'set-form-err', hidden: true });
  const archiveBtn = el('button', {
    type: 'button',
    class: 'set-btn set-danger',
    onClick: () => armDestructive(archiveBtn, {
      idle: 'Archive product',
      armed: 'Archive this product',
      onConfirm: () => doArchive(row, modal, opts, err, archiveBtn),
    }),
  }, 'Archive product');
  const body = el('div', { class: 'set-form' },
    err,
    el('label', { class: 'set-field' },
      el('span', {}, 'Name'),
      el('input', {
        class: 'set-input',
        type: 'text',
        value: form.name,
        onInput: (event) => { form.name = event.target.value; },
      })),
    el('label', { class: 'set-field' },
      el('span', {}, 'Slug'),
      el('input', {
        class: 'set-input',
        type: 'text',
        value: form.slug,
        onInput: (event) => { form.slug = event.target.value; },
      })),
    el('label', { class: 'set-field' },
      el('span', {}, 'Description'),
      el('textarea', {
        class: 'set-textarea',
        rows: 3,
        value: form.description,
        onInput: (event) => { form.description = event.target.value; },
      })),
    el('p', { class: 'set-hint' },
      'Archive hides this product from the switcher. Chats, knowledge, and connectors are kept so you can restore later.'),
    el('div', { class: 'set-form-actions' },
      archiveBtn,
      el('button', {
        type: 'button',
        class: 'set-btn set-btn-primary',
        onClick: () => saveProduct(row, form, err, modal, opts, archiveBtn),
      }, 'Save changes')));
  const modal = openModal({
    title: 'Edit product',
    className: 'set-modal',
    body,
    onClose: opts.onClose,
  });
  return modal;
}

async function saveProduct(row, form, err, modal, opts, archiveBtn) {
  resetDestructive(archiveBtn, 'Archive product');
  err.hidden = true;
  const name = String(form.name || '').trim();
  const slug = String(form.slug || '').trim();
  if (!name || !slug) {
    err.textContent = 'Name and slug are required';
    err.hidden = false;
    return;
  }
  const result = await updateProduct(row.id, {
    name,
    slug,
    description: String(form.description || '').trim(),
  });
  if (!result || result.error) {
    err.textContent = (result && result.error) || 'Could not update product';
    err.hidden = false;
    return;
  }
  toast('Product updated');
  modal.close();
  if (opts.onDone) await opts.onDone();
}

async function doArchive(row, modal, opts, err, archiveBtn) {
  err.hidden = true;
  archiveBtn.disabled = true;
  const result = await archiveProduct(row.id);
  archiveBtn.disabled = false;
  if (!result || result.error) {
    resetDestructive(archiveBtn, 'Archive product');
    err.textContent = (result && result.error) || 'Could not archive product';
    err.hidden = false;
    return;
  }
  toast('Product archived');
  modal.close();
  if (opts.onDone) await opts.onDone();
}
